import React, { useEffect, useRef } from 'react'
import { CobuStack, CobuText, PerksListSkeleton } from '@cobu/ui'
import { useTheme } from 'tamagui'
import { useNavigate } from 'react-router-dom'
import { PerkFilterType, PerkData } from '@cobu/types'
import { usePerks } from '../../hooks/usePerks'
import { Perk } from '../../components/perks/Perk'
import { Strings } from '../../constants/Strings'
import { analyticsService } from '@cobu/analytics'
import { showErrorMessage } from '../../utils/messageToast'
import { PullToRefreshWrapper } from '../../components/ui/PullToRefreshWrapper'

const strings = Strings.get();

export default function PerksScreen() {
  const navigate = useNavigate()
  const theme = useTheme()
  const {
    currentFilter,
    refreshing,
    perks,
    isLoading,
    error,
    handleFilterPress,
    onRefresh,
    onPressListItem,
  } = usePerks()

  React.useEffect(() => {
    analyticsService.trackEvent('VIEW_PERKS_TAB')
  }, [])

  const renderPerks = () => {
    if (isLoading) {
      return <PerksListSkeleton />
    } else if (error) {
      return <ErrorState />
    } else if (perks.length === 0) {
      return <EmptyState currentFilter={currentFilter} />
    } else {
      return (
        <PerksList
          perks={perks}
          onPerkPress={onPressListItem}
        />
      )
    }
  }

  return (
    <div style={{ flex: 1, backgroundColor: "#f6f6f6"}}>
      <PullToRefreshWrapper
        onRefresh={onRefresh}
        isRefreshing={refreshing}
        useWindowScroll={false}
      >
        <CobuStack f={1} px="$0" style={{ overflow: 'auto', height: '100%' }}>
          {renderFilterButtons({ currentFilter, handleFilterPress })}
          {renderPerks()}
          {/* <UniversalButtonContainer /> */}
        </CobuStack>
      </PullToRefreshWrapper>
    </div>
  )
}

function ErrorState() {
  const shownRef = useRef(false)
  useEffect(() => {
    if (!shownRef.current) {
      showErrorMessage(strings.common.somethingWentWrong)
      shownRef.current = true
    }
  }, [])
  return (
    <CobuStack ai="center" jc="center" p="$4">
      <CobuText color="$error">{strings.common.somethingWentWrong}</CobuText>
    </CobuStack>
  )
}

function EmptyState({ currentFilter }: { currentFilter: PerkFilterType }) {
  return (
    <CobuStack f={1} jc="center" ai="center" gap="$4">
      <CobuText variant="heading" color="$cobuDarkBlue">
        {currentFilter === 'all' ? strings.perks.states.empty.allPerks : strings.perks.states.empty.savedPerks}
      </CobuText>
    </CobuStack>
  )
}

function PerksList({
  perks,
  onPerkPress,
}: {
  perks: PerkData[]
  onPerkPress: (perkId: string, heroColor?: string) => void
}) {
  return (
    <CobuStack f={1} gap="$2" px="$3" py="$2">
      {perks.map((perk, index) => (
        <Perk key={perk.uuid} perk={perk} index={index} onPerkPress={onPerkPress} />
      ))}
    </CobuStack>
  )
}

function renderFilterButtons({
  currentFilter,
  handleFilterPress,
}: {
  currentFilter: PerkFilterType
  handleFilterPress: (filter: PerkFilterType) => void
}) {
  return (
    <CobuStack layout="horizontal" gap="$0" mx="$4" mt="$3" mb="$2">
      <CobuStack
        f={1}
        mih={'$7'}
        h="auto"
        ai="center"
        jc="center"
        bg={currentFilter === 'all' ? '#000000' : 'transparent'}
        br="$6"
        onPress={() => handleFilterPress('all')}
        role="button"
        tabIndex={0}
        style={{ WebkitTapHighlightColor: 'transparent' as any }}
      >
        <CobuText color={currentFilter === 'all' ? '#ffffff' : 'rgba(0,0,0,0.7)'} fow="500" fos="$2">
          {strings.perks.filters.all}
        </CobuText>
      </CobuStack>
      <CobuStack
        f={1}
        mih={'$7'}
        h="auto"
        ai="center"
        jc="center"
        bg={currentFilter === 'saved' ? '#000000' : 'transparent'}
        br="$6"
        onPress={() => handleFilterPress('saved')}
        role="button"
        tabIndex={0}
        style={{ WebkitTapHighlightColor: 'transparent' as any }}
      >
        <CobuText color={currentFilter === 'saved' ? '#ffffff' : 'rgba(0,0,0,0.7)'} fow="500" fos="$2">
          {strings.perks.filters.saved}
        </CobuText>
      </CobuStack>
    </CobuStack>
  )
}
